import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
} from "@mui/material";
import { LoadingBox } from "@components/LoadingBox";
import { PAGE_CONTAINER_SX } from "@/constants/layout";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as matchesApi from "@api/matches.api";
import * as matchEventsApi from "@api/match-events.api";
import type { Match } from "@/types";

interface MatchWithScore extends Match {
  score: { red: number; blue: number };
}

export default function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [matches, setMatches] = useState<MatchWithScore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await matchesApi.getMatches(true);
        const withScores = await Promise.all(
          data.map(async (m) => {
            const score = await matchEventsApi.getMatchScore(m.id);
            return { ...m, score };
          }),
        );
        setMatches(withScores);
      } catch (err) {
        notify(extractErrorMessage(err), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [notify]);

  if (loading) {
    return <LoadingBox />;
  }

  return (
    <Box sx={PAGE_CONTAINER_SX}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("history.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t("history.subtitle")}
      </Typography>
      {matches.length === 0 ? (
        <Typography
          sx={{
            color: "text.secondary",
            textAlign: "center",
            py: 6,
          }}
        >
          {t("history.noMatches")}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {matches.map((m) => (
            <Card key={m.id}>
              <CardActionArea onClick={() => navigate(`/matches/${m.id}/live`)}>
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {new Date(m.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={t("teams.redTeam")}
                      color="error"
                      size="small"
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      {m.score.red} - {m.score.blue}
                    </Typography>
                    <Chip
                      label={t("teams.blueTeam")}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Chip
                    label={t("history.completed")}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
